// P12 — Fila admin de pedidos de publicação de agentes.
//
// 2 colunas: lista PENDING à esquerda, detalhe à direita (agente completo,
// arquivos, justificativa, botões approve/reject). "Testar agente" abre
// `/agentes/<id>` em nova aba — usa a UI normal do chat, com conversa
// persistida do admin (decisão consciente: substitui o "ChatTestDrawer
// com conversa temporária" do prompt original; mais simples e suficiente
// pra revisão, sem inventar caminho novo de chat).

import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import adminAgentsService from '@/services/adminAgents.service';
import { useToast } from '@/contexts/ToastContext';
import {
  AgentPublicationRequest,
  AgentPublicationRequestDetail,
} from '@/types/agents';
import AgentAvatar from '@/components/AgentAvatar';

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(s: string): string {
  return new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function ScopeBadge({ scope }: { scope: 'SECTOR' | 'GLOBAL' }) {
  const colors =
    scope === 'GLOBAL'
      ? 'bg-purple-500/20 text-purple-200 border-purple-500/30'
      : 'bg-blue-500/20 text-blue-200 border-blue-500/30';
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs border ${colors}`}>
      {scope === 'GLOBAL' ? 'Global' : 'Setor'}
    </span>
  );
}

interface RequestCardProps {
  request: AgentPublicationRequest;
  selected: boolean;
  onClick: () => void;
}

const RequestCard: FC<RequestCardProps> = ({ request, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`request-card-${request.id}`}
    className={`w-full text-left rounded-lg border px-3 py-3 transition ${
      selected
        ? 'border-accent bg-accent/10'
        : 'border-surface-border bg-surface-secondary hover:border-surface-border/60'
    }`}
  >
    <div className="flex items-center gap-2 mb-1">
      <AgentAvatar avatar={request.agent.avatar} sizeClass="w-8 h-8" textClass="text-lg" />
      <span className="font-semibold text-content-primary text-sm truncate">
        {request.agent.name}
      </span>
      <ScopeBadge scope={request.target_scope} />
    </div>
    <p className="text-xs text-content-tertiary line-clamp-2">{request.justification}</p>
    <p className="text-2xs text-content-tertiary mt-1">
      {request.requested_by.name} · {fmtDate(request.created_at)}
    </p>
  </button>
);

interface RequestDetailProps {
  detail: AgentPublicationRequestDetail;
  onApprove: (notes: string) => Promise<void>;
  onReject: (notes: string) => Promise<void>;
  onTest: () => void;
  busy: boolean;
}

const RequestDetail: FC<RequestDetailProps> = ({
  detail,
  onApprove,
  onReject,
  onTest,
  busy,
}) => {
  const [notes, setNotes] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);
  const a = detail.agent;

  return (
    <div className="space-y-5" data-testid="request-detail">
      <header className="flex items-start gap-3">
        <AgentAvatar avatar={a.avatar} />
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-content-primary truncate">{a.name}</h2>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <ScopeBadge scope={detail.target_scope} />
            {detail.target_sectors.map((s) => (
              <span
                key={s.id}
                className="text-xs px-2 py-0.5 rounded-full bg-surface-secondary border border-surface-border text-content-primary"
              >
                {s.name}
              </span>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={onTest}
          className="inline-flex items-center gap-1 rounded-lg border border-surface-border px-3 py-2 text-sm text-content-primary hover:bg-surface-secondary"
          data-testid="test-agent"
        >
          <span className="material-symbols-outlined text-base">open_in_new</span>
          Testar (nova aba)
        </button>
      </header>

      <section className="rounded-lg border border-surface-border bg-surface-secondary p-3 space-y-1 text-sm">
        <p>
          <span className="text-content-tertiary">Pedido por: </span>
          <strong className="text-content-primary">
            {detail.requested_by.name}
            {a.owner_is_active === false && (
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-red-500/20 text-red-200 border border-red-500/30">
                inativo
              </span>
            )}
          </strong>
        </p>
        <p>
          <span className="text-content-tertiary">Modelo: </span>
          <strong className="text-content-primary">{a.model}</strong>
          <span className="text-content-tertiary"> · Temperatura: </span>
          <strong className="text-content-primary">{a.temperature}</strong>
        </p>
        <p>
          <span className="text-content-tertiary">Criado em: </span>
          {fmtDate(detail.created_at)}
        </p>
      </section>

      <section>
        <h3 className="text-sm font-semibold text-content-primary mb-1">Justificativa</h3>
        <blockquote className="rounded-lg border-l-4 border-accent bg-surface-secondary p-3 text-sm text-content-primary whitespace-pre-wrap">
          {detail.justification}
        </blockquote>
      </section>

      <section>
        <button
          type="button"
          onClick={() => setShowPrompt((v) => !v)}
          className="text-sm text-content-tertiary hover:text-content-primary"
          data-testid="toggle-prompt"
        >
          {showPrompt ? '▾' : '▸'} System prompt
        </button>
        {showPrompt && (
          <pre
            data-testid="system-prompt"
            className="mt-2 rounded-lg bg-surface-secondary border border-surface-border p-3 text-xs whitespace-pre-wrap font-mono text-content-primary max-h-80 overflow-y-auto"
          >
            {a.system_prompt || '(vazio)'}
          </pre>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-content-primary mb-2">
          Arquivos ({detail.files.length})
        </h3>
        {detail.files.length === 0 ? (
          <p className="text-sm text-content-tertiary">Sem arquivos anexados.</p>
        ) : (
          <ul className="space-y-1" data-testid="files-list">
            {detail.files.map((f) => (
              <li
                key={f.id}
                className="flex items-center gap-2 text-sm rounded border border-surface-border bg-surface-secondary px-2 py-1.5"
              >
                <span className="material-symbols-outlined text-base text-content-tertiary">
                  description
                </span>
                <span className="flex-1 truncate text-content-primary">{f.filename}</span>
                <span className="text-xs text-content-tertiary">{fmtBytes(f.size_bytes)}</span>
                <span className="text-xs text-content-tertiary">{f.chunk_count} chunks</span>
                <span
                  className={`text-2xs uppercase font-bold ${
                    f.status === 'READY'
                      ? 'text-green-400'
                      : f.status === 'FAILED'
                        ? 'text-red-400'
                        : 'text-amber-400'
                  }`}
                >
                  {f.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-2 pt-2 border-t border-surface-border">
        <label className="block">
          <span className="text-sm font-semibold text-content-primary block mb-1">
            Notas (opcional)
          </span>
          <textarea
            data-testid="review-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Visível pro owner do agente — explique o motivo se rejeitar."
            className="w-full resize-none rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary text-sm"
          />
        </label>
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => onReject(notes)}
            disabled={busy}
            data-testid="reject"
            className="rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            Rejeitar
          </button>
          <button
            type="button"
            onClick={() => onApprove(notes)}
            disabled={busy}
            data-testid="approve"
            className="rounded-lg bg-green-500/90 hover:bg-green-500 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
          >
            Aprovar
          </button>
        </div>
      </section>
    </div>
  );
};

const AdminAgentApprovalsPage: FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [requests, setRequests] = useState<AgentPublicationRequest[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<AgentPublicationRequestDetail | null>(null);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await adminAgentsService.listPublicationRequests({ status: 'PENDING' });
      setRequests(data);
      // Mantém o selecionado se ainda estiver na lista
      if (selectedId && !data.find((r) => r.id === selectedId)) {
        setSelectedId(null);
        setDetail(null);
      }
    } catch {
      showToast('Não foi possível carregar a fila de aprovações.', 'error');
    } finally {
      setLoadingList(false);
    }
  }, [selectedId, showToast]);

  useEffect(() => {
    void refresh();
    // refresh é estável o suficiente — depende só de selectedId, que muda
    // por clique do user (intencionalmente recarrega lista nesses casos)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setLoadingDetail(true);
    adminAgentsService
      .getPublicationRequest(selectedId)
      .then(setDetail)
      .catch(() => showToast('Erro ao carregar detalhe.', 'error'))
      .finally(() => setLoadingDetail(false));
  }, [selectedId, showToast]);

  const handleApprove = async (notes: string) => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await adminAgentsService.approvePublication(selectedId, notes);
      showToast('Pedido aprovado.', 'success');
      setSelectedId(null);
      void refresh();
    } catch {
      showToast('Erro ao aprovar.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleReject = async (notes: string) => {
    if (!selectedId) return;
    setBusy(true);
    try {
      await adminAgentsService.rejectPublication(selectedId, notes);
      showToast('Pedido rejeitado.', 'success');
      setSelectedId(null);
      void refresh();
    } catch {
      showToast('Erro ao rejeitar.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleTest = () => {
    if (detail) window.open(`/agentes/${detail.agent.id}`, '_blank', 'noopener');
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="admin-agent-approvals-page">
      <header className="mb-6">
        <button
          type="button"
          onClick={() => navigate('/admin/agentes')}
          className="text-sm text-content-tertiary hover:text-content-primary mb-2"
        >
          ← Voltar pra agentes
        </button>
        <h1 className="text-2xl font-bold text-content-primary">
          Aprovações de publicação
          <span className="ml-2 text-base font-normal text-content-tertiary">
            ({requests.length})
          </span>
        </h1>
        <p className="text-sm text-content-tertiary mt-1">
          Revise agentes pessoais que querem ser promovidos a setor ou global.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <aside className="lg:col-span-4">
          {loadingList ? (
            <p className="text-sm text-content-tertiary">Carregando…</p>
          ) : requests.length === 0 ? (
            <p
              data-testid="empty-list"
              className="text-sm text-content-tertiary rounded-lg border border-dashed border-surface-border p-6 text-center"
            >
              Nenhum pedido pendente.
            </p>
          ) : (
            <ul className="space-y-2">
              {requests.map((r) => (
                <li key={r.id}>
                  <RequestCard
                    request={r}
                    selected={selectedId === r.id}
                    onClick={() => setSelectedId(r.id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="lg:col-span-8">
          {loadingDetail ? (
            <p className="text-sm text-content-tertiary">Carregando detalhe…</p>
          ) : detail ? (
            <RequestDetail
              detail={detail}
              onApprove={handleApprove}
              onReject={handleReject}
              onTest={handleTest}
              busy={busy}
            />
          ) : (
            <div
              data-testid="empty-detail"
              className="rounded-lg border border-dashed border-surface-border p-12 text-center text-content-tertiary"
            >
              Selecione um pedido pra revisar.
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminAgentApprovalsPage;
